import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listTenants, updateTenant } from '../../api/superAdmin';
import type { TenantInfo } from '../../api/superAdmin';
import styles from './TenantsPanel.module.css';
import adminStyles from './AdminPage.module.css';

const TIERS = ['free', 'professional', 'enterprise'] as const;
const STATUSES = ['active', 'suspended'] as const;

export function TenantsPanel() {
  const { t } = useTranslation(['admin', 'common']);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingMaxUsers, setEditingMaxUsers] = useState<Record<string, string>>({});

  useEffect(() => {
    loadTenants();
  }, []);

  async function loadTenants() {
    try {
      setLoading(true);
      const data = await listTenants();
      setTenants(data);
    } catch {
      // handle
    } finally {
      setLoading(false);
    }
  }

  async function handleTierChange(tenant: TenantInfo, tier: string) {
    setSaving(tenant.id);
    try {
      await updateTenant(tenant.id, { tier });
      await loadTenants();
    } catch {
      // handle
    } finally {
      setSaving(null);
    }
  }

  async function handleStatusChange(tenant: TenantInfo, status: string) {
    setSaving(tenant.id);
    try {
      await updateTenant(tenant.id, { status });
      await loadTenants();
    } catch {
      // handle
    } finally {
      setSaving(null);
    }
  }

  async function handleMaxUsersBlur(tenant: TenantInfo) {
    const val = editingMaxUsers[tenant.id];
    if (val === undefined) return;
    const num = parseInt(val, 10);
    if (isNaN(num) || num < 1 || num === tenant.max_users) {
      setEditingMaxUsers((prev) => {
        const next = { ...prev };
        delete next[tenant.id];
        return next;
      });
      return;
    }
    setSaving(tenant.id);
    try {
      await updateTenant(tenant.id, { max_users: num });
      await loadTenants();
    } catch {
      // handle
    } finally {
      setSaving(null);
      setEditingMaxUsers((prev) => {
        const next = { ...prev };
        delete next[tenant.id];
        return next;
      });
    }
  }

  if (loading) return <div className={adminStyles.empty}>{t('admin:tenantsLoading')}</div>;

  return (
    <div>
      {tenants.length === 0 ? (
        <div className={adminStyles.empty}>{t('admin:tenantsEmpty')}</div>
      ) : (
        <table className={adminStyles.table}>
          <thead>
            <tr>
              <th>{t('admin:tenantName')}</th>
              <th>{t('admin:tenantSlug')}</th>
              <th>{t('admin:tenantTier')}</th>
              <th>{t('admin:tenantStatus')}</th>
              <th>{t('admin:tenantMaxUsers')}</th>
              <th>{t('admin:tenantEmail')}</th>
              <th>{t('admin:tenantCreated')}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.id} style={{ opacity: saving === tenant.id ? 0.6 : 1 }}>
                <td>{tenant.name}</td>
                <td><code className={styles.slug}>{tenant.slug}</code></td>
                <td>
                  <select
                    className={styles.select}
                    value={tenant.tier}
                    onChange={(e) => handleTierChange(tenant, e.target.value)}
                    disabled={saving === tenant.id}
                  >
                    {TIERS.map((tier) => (
                      <option key={tier} value={tier}>{tier}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className={`${styles.select} ${tenant.status === 'active' ? styles.statusActive : styles.statusSuspended}`}
                    value={tenant.status}
                    onChange={(e) => handleStatusChange(tenant, e.target.value)}
                    disabled={saving === tenant.id}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className={styles.maxUsersInput}
                    type="number"
                    min={1}
                    value={editingMaxUsers[tenant.id] ?? tenant.max_users}
                    onChange={(e) =>
                      setEditingMaxUsers((prev) => ({ ...prev, [tenant.id]: e.target.value }))
                    }
                    onBlur={() => handleMaxUsersBlur(tenant)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                    disabled={saving === tenant.id}
                  />
                </td>
                <td className={styles.emailCell}>{tenant.contact_email}</td>
                <td>{new Date(tenant.created_at).toLocaleDateString('fr-CH')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
